/* eslint-disable tailwindcss/no-custom-classname, tailwindcss/no-arbitrary-value */
import { RawHtml } from '../../components/RawHtml/RawHtml';
import type { PolicySummaryCardProps, PolicySummaryData } from './types';

/**
 * Policy Summary Card (CMS-1794) — the post-purchase policy details card on the
 * TI E2E confirmation page. Provider logo + product name, an ordered list of
 * label/value rows, and an editor-configured delivery note.
 *
 * Fully presentational: the fe-services wrapper resolves the rows per vertical
 * (CMS-1821), formats every value (currency/date/travellers by market+locale),
 * and picks the delivery note by purchase status. This component renders exactly
 * what it is given, or a skeleton while the client-side purchase data resolves.
 *
 * Layout follows the confirmation Figma: header (logo box + name) then a single
 * column of rows with a divider under each; the note sits below. Same colour /
 * type values as the current success page (label #616161, value #212121,
 * divider #e0e0e0) so the migrated card reads identically to the journey.
 */
export const PolicySummaryCard = ({
  summary,
}: PolicySummaryCardProps): JSX.Element => (
  <section
    data-component="policy-summary-card"
    className="flex flex-col gap-4 rounded-2xl border border-fill/alternative-3 bg-white p-4 xl:gap-6 xl:rounded-3xl xl:p-6"
  >
    {summary ? <CardContent summary={summary} /> : <CardSkeleton />}
  </section>
);

const CardContent = ({
  summary,
}: {
  summary: PolicySummaryData;
}): JSX.Element => (
  <>
    <div className="flex flex-col items-center gap-4 md:flex-row xl:gap-6">
      <span className="flex h-20 w-full shrink-0 items-center justify-center overflow-hidden rounded-lg border border-fill/alternative-3 p-4 md:w-50 xl:h-28 xl:w-68 xl:rounded-2xl xl:p-6">
        {summary.providerLogoUrl ? (
          // Runtime provider/product image from the purchase API (external host,
          // not on the Next image allowlist), so a raw <img> rather than CmsImage
          // - matches the apply flow's handling of the same `productImage` field.
          <img
            src={summary.providerLogoUrl}
            alt={summary.providerName}
            width={160}
            height={80}
            className="max-h-full w-auto object-contain"
          />
        ) : (
          // Logo missing / failed — fall back to provider name (CMS-1821).
          <span className="text-center font-[600] text-text/primary text-[16px] leading-[22px]">
            {summary.providerName}
          </span>
        )}
      </span>
      <p className="text-center font-[700] text-text/primary text-[22px] leading-[28px] md:text-left xl:text-[28px] xl:leading-[34px]">
        {summary.title}
      </p>
    </div>

    {summary.rows.length > 0 && (
      <dl className="flex flex-col">
        {summary.rows.map((row) => (
          <div
            key={row.id}
            data-component="policy-summary-row"
            className="flex items-start gap-4 border-b border-fill/alternative-3 py-3 last:border-b-0 xl:gap-6 xl:py-4"
          >
            <dt className="w-39 shrink-0 font-[500] text-text/secondary-lighter text-[14px] leading-[20px] md:w-46 xl:text-[18px] xl:leading-[24px]">
              {row.label}
            </dt>
            <dd className="font-[600] text-text/primary text-[14px] leading-[20px] xl:text-[18px] xl:leading-[24px]">
              {row.value}
            </dd>
          </div>
        ))}
      </dl>
    )}

    {summary.deliveryNoteHtml && (
      <RawHtml
        content={summary.deliveryNoteHtml}
        className="font-[500] text-text/secondary-lighter text-[14px] leading-[20px] xl:text-[18px] xl:leading-[24px]"
      />
    )}
  </>
);

// Whole-card skeleton shown until the client resolves the purchase data:
// header (logo + name), a fixed set of row bars, and the note slot all pulse.
const CardSkeleton = (): JSX.Element => (
  <span
    data-component="policy-summary-card-skeleton"
    aria-hidden
    className="flex flex-col gap-4 xl:gap-6"
  >
    <span className="flex flex-col items-center gap-4 md:flex-row xl:gap-6">
      <span className="h-20 w-full shrink-0 animate-pulse rounded-lg bg-fill/alternative-2 md:w-50 xl:h-28 xl:w-68" />
      <span className="h-7 w-3/5 animate-pulse rounded-md bg-fill/alternative-2" />
    </span>
    <span className="flex flex-col gap-6 py-1">
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <span
          key={index}
          className="h-4 w-full animate-pulse rounded-md bg-fill/alternative-2"
        />
      ))}
    </span>
    <span className="h-10 w-full animate-pulse rounded-md bg-fill/alternative-2" />
  </span>
);
